import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  DELEGABLE_ROLE_CODES,
  type DelegableRoleCode,
  type Delegation,
  type DelegationStatus,
  type GrantDelegationRequest,
  type InheritedPermissionReview,
} from '@/types/platform';
import { formatDate, formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-12 — delegated product administration.
 *
 * THE FORM'S JOB IS TO MAKE THE BOUNDARIES VISIBLE, NOT TO DISCOVER THEM.
 * The server refuses a delegation that widens authority: an undelegable role, an
 * unscoped grant, a scope wider than the delegator holds, onward delegation of
 * already-delegated authority, or an approval limit above the delegator's own. A
 * form that offers all of those and then relays a 400 has taught the operator that
 * the rules are arbitrary and that the way to work is to try things. So the role
 * select only contains delegable roles, the institution field is required rather
 * than optional-with-a-surprise, and the rules themselves are stated on screen
 * before anything is typed.
 *
 * WHAT THE SERVER STILL DECIDES ALONE
 * Whether the delegator actually holds the authority at that scope, and whether
 * the approval limit is within theirs, depend on data this console does not have.
 * Those are checked server-side, again at approval time — the delegator's own
 * authority can be revoked between request and approval, and an approval that
 * trusted the original decision would make the waiting period the attack.
 */

const STATUS_TONES: Record<DelegationStatus, string> = {
  PENDING: 'warnTag',
  ACTIVE: 'okTag',
  REVOKED: 'mutedTag',
  EXPIRED: 'mutedTag',
};

export function DelegationsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [params, setParams] = useSearchParams();
  const institutionId = params.get('institutionId') ?? '';

  const fetcher = useCallback(() => platformApi.delegations.list(institutionId), [institutionId]);
  const { state, reload } = useAsyncData<Delegation[]>(
    `delegations:${institutionId}`,
    fetcher,
    institutionId.length > 0,
  );

  const [grantOpen, setGrantOpen] = useState(false);
  const [reviewing, setReviewing] = useState<Delegation | null>(null);
  const [revoking, setRevoking] = useState<Delegation | null>(null);
  const [approving, setApproving] = useState<Delegation | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.delegations.title')}</h1>
          <p className={styles.pageLead}>{t('platform.delegations.lead')}</p>
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setGrantOpen(true)}
          disabled={institutionId.length === 0}
        >
          <Icon name="plus" size={16} />
          {t('platform.delegations.grant')}
        </button>
      </header>

      <EscalationRules />

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="delegation-institution">
            {t('platform.delegations.institutionLabel')}
          </label>
          {/* Picking applies immediately — no UUID to paste, no submit to press. */}
          <InstitutionPicker
            id="delegation-institution"
            value={institutionId}
            clearable
            onChange={(next) => setParams(next ? { institutionId: next } : {})}
          />
        </div>
      </div>

      {institutionId.length === 0 ? (
        <p className={styles.sectionEmpty} role="status">
          {t('platform.delegations.chooseInstitution')}
        </p>
      ) : (
        <AsyncPanel
          state={state}
          onRetry={reload}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.delegations.emptyTitle')}
          emptyBody={t('platform.delegations.emptyBody')}
        >
          {(data) => (
            <ul className={styles.rowList}>
              {data.map((delegation) => (
                <li key={delegation.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <p className={styles.rowTitle}>
                      {delegation.delegateName ?? delegation.delegatePersonId}
                      {' — '}
                      {delegation.delegatedRoleCode ?? t('platform.delegations.unknownRole')}
                    </p>
                    <p className={styles.rowMetaLine}>
                      {t('platform.delegations.from', {
                        who: delegation.delegatorName ?? delegation.delegatorPersonId,
                      })}
                      {' · '}
                      {t('platform.delegations.window', {
                        from: formatDate(delegation.validFrom, locale),
                        to: formatDate(delegation.validUntil, locale),
                      })}
                      {delegation.campusId ? ` · ${t('platform.delegations.campusScoped')}` : ''}
                      {typeof delegation.approvalLimit === 'number'
                        ? ` · ${t('platform.delegations.limit', {
                            amount: delegation.approvalLimit,
                            currency: delegation.currency ?? '',
                          })}`
                        : ''}
                    </p>
                    <p className={styles.rowDetail}>{delegation.reason}</p>
                    {delegation.revokedAt && (
                      <p className={styles.rowDetail}>
                        {t('platform.delegations.revokedAt', {
                          when: formatDateTime(delegation.revokedAt, locale),
                        })}
                      </p>
                    )}
                  </div>
                  <div className={styles.rowAside}>
                    <span className={styles[STATUS_TONES[delegation.status] ?? 'mutedTag']}>
                      {t(`platform.delegations.status.${delegation.status}`, { defaultValue: delegation.status })}
                    </span>
                    <button type="button" className={styles.linkBtn} onClick={() => setReviewing(delegation)}>
                      {t('platform.delegations.review')}
                    </button>
                    {delegation.status === 'PENDING' && (
                      <button type="button" className={styles.linkBtn} onClick={() => setApproving(delegation)}>
                        {t('platform.delegations.approve')}
                      </button>
                    )}
                    {(delegation.status === 'PENDING' || delegation.status === 'ACTIVE') && (
                      <button type="button" className={styles.linkBtnDanger} onClick={() => setRevoking(delegation)}>
                        {t('platform.delegations.revoke')}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </AsyncPanel>
      )}

      {rowError && (
        <p className={styles.formError} role="alert">
          {rowError}
        </p>
      )}

      <GrantDialog
        open={grantOpen}
        institutionId={institutionId}
        onClose={() => setGrantOpen(false)}
        onGranted={() => {
          setGrantOpen(false);
          reload();
        }}
      />

      <ReviewDialog delegation={reviewing} onClose={() => setReviewing(null)} locale={locale} />

      <ApproveDialog
        delegation={approving}
        onClose={() => setApproving(null)}
        onApproved={() => {
          setApproving(null);
          reload();
        }}
        onError={setRowError}
      />

      <RevokeDialog
        delegation={revoking}
        onClose={() => setRevoking(null)}
        onRevoked={() => {
          setRevoking(null);
          reload();
        }}
      />
    </div>
  );
}

/**
 * The escalation boundaries, stated before anything is typed.
 *
 * This is not decoration. Every one of these is a case the server refuses, and an
 * operator who meets them one 400 at a time concludes the system is capricious.
 */
function EscalationRules() {
  const { t } = useTranslation();
  const rules = ['delegable', 'scoped', 'contained', 'noOnward', 'limit', 'reapproved'] as const;

  return (
    <Panel title={t('platform.delegations.rulesTitle')} icon="noAccess">
      <ul className={styles.ruleList}>
        {rules.map((rule) => (
          <li key={rule} className={styles.ruleItem}>
            <Icon name="ban" size={14} aria-hidden="true" />
            <span>{t(`platform.delegations.rule.${rule}`)}</span>
          </li>
        ))}
      </ul>
      <p className={styles.fieldHint}>
        {t('platform.delegations.rulesRoles', { roles: DELEGABLE_ROLE_CODES.join(', ') })}
      </p>
    </Panel>
  );
}

/* ─── Grant ─── */

const MIN_REASON = 10;

function GrantDialog({
  open,
  institutionId,
  onClose,
  onGranted,
}: {
  open: boolean;
  institutionId: string;
  onClose: () => void;
  onGranted: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<GrantDelegationRequest>(() => emptyGrant(institutionId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyGrant(institutionId));
      setError(null);
    }
  }, [open, institutionId]);

  const update = <K extends keyof GrantDelegationRequest>(field: K, value: GrantDelegationRequest[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  const windowValid =
    form.validFrom.length > 0 && form.validUntil.length > 0 && form.validUntil > form.validFrom;
  const valid =
    form.delegatorPersonId.trim().length > 0 &&
    form.delegatePersonId.trim().length > 0 &&
    form.delegatePersonId.trim() !== form.delegatorPersonId.trim() &&
    form.sourceRoleAssignmentId.trim().length > 0 &&
    form.institutionId.trim().length > 0 &&
    windowValid &&
    form.reason.trim().length >= MIN_REASON;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.delegations.grant({
        ...form,
        campusId: form.campusId?.trim() || undefined,
        approvalLimit: form.approvalLimit,
        currency: form.currency?.trim() || undefined,
        validFrom: new Date(form.validFrom).toISOString(),
        validUntil: new Date(form.validUntil).toISOString(),
      });
      onGranted();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.delegations.grantTitle')}
      description={t('platform.delegations.grantLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.delegations.fieldDelegator')}</span>
            <input
              className={styles.input}
              value={form.delegatorPersonId}
              required
              onChange={(event) => update('delegatorPersonId', event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.delegations.fieldDelegate')}</span>
            <input
              className={styles.input}
              value={form.delegatePersonId}
              required
              aria-invalid={
                form.delegatePersonId.length > 0 && form.delegatePersonId.trim() === form.delegatorPersonId.trim()
              }
              onChange={(event) => update('delegatePersonId', event.target.value)}
            />
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.delegations.fieldSourceAssignment')}</span>
          <input
            className={styles.input}
            value={form.sourceRoleAssignmentId}
            required
            aria-describedby="delegation-source-hint"
            onChange={(event) => update('sourceRoleAssignmentId', event.target.value)}
          />
          <span className={styles.fieldHint} id="delegation-source-hint">
            {t('platform.delegations.fieldSourceHint')}
          </span>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.delegations.fieldRole')}</span>
          <Select
            value={form.delegatedRoleCode}
            onChange={(value) => update('delegatedRoleCode', value as DelegableRoleCode)}
            options={DELEGABLE_ROLE_CODES.map((code) => ({ value: code, label: code }))}
          />
          <span className={styles.fieldHint}>{t('platform.delegations.fieldRoleHint')}</span>
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.delegations.fieldInstitution')}</span>
            <input
              className={styles.input}
              value={form.institutionId}
              required
              onChange={(event) => update('institutionId', event.target.value)}
            />
            <span className={styles.fieldHint}>{t('platform.delegations.fieldInstitutionHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.delegations.fieldCampus')}</span>
            <input
              className={styles.input}
              value={form.campusId ?? ''}
              onChange={(event) => update('campusId', event.target.value)}
            />
            <span className={styles.fieldHint}>{t('platform.delegations.fieldCampusHint')}</span>
          </label>
        </div>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.delegations.fieldValidFrom')}</span>
            <input
              className={styles.input}
              type="date"
              value={form.validFrom}
              required
              max={form.validUntil || undefined}
              onChange={(event) => update('validFrom', event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.delegations.fieldValidUntil')}</span>
            <input
              className={styles.input}
              type="date"
              value={form.validUntil}
              required
              min={form.validFrom || undefined}
              aria-invalid={form.validUntil.length > 0 && !windowValid}
              onChange={(event) => update('validUntil', event.target.value)}
            />
            <span className={styles.fieldHint}>{t('platform.delegations.fieldValidUntilHint')}</span>
          </label>
        </div>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.delegations.fieldLimit')}</span>
            <input
              className={styles.input}
              type="number"
              min={0}
              step="0.01"
              value={form.approvalLimit ?? ''}
              onChange={(event) =>
                update('approvalLimit', event.target.value === '' ? undefined : Number(event.target.value))
              }
            />
            <span className={styles.fieldHint}>{t('platform.delegations.fieldLimitHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.delegations.fieldCurrency')}</span>
            <input
              className={styles.input}
              value={form.currency ?? ''}
              maxLength={3}
              onChange={(event) => update('currency', event.target.value.toUpperCase())}
            />
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.delegations.fieldReason')}</span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={form.reason}
            required
            aria-invalid={form.reason.length > 0 && form.reason.trim().length < MIN_REASON}
            onChange={(event) => update('reason', event.target.value)}
          />
          <span className={styles.fieldHint}>{t('platform.delegations.fieldReasonHint', { min: MIN_REASON })}</span>
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.delegations.granting') : t('platform.delegations.grant')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function emptyGrant(institutionId: string): GrantDelegationRequest {
  return {
    delegatorPersonId: '',
    delegatePersonId: '',
    sourceRoleAssignmentId: '',
    delegatedRoleCode: DELEGABLE_ROLE_CODES[0],
    institutionId,
    campusId: '',
    validFrom: '',
    validUntil: '',
    currency: '',
    reason: '',
  };
}

/* ─── Review ─── */

/**
 * What the delegation actually confers.
 *
 * Fetched rather than derived from the role code: the answer depends on the
 * permission rows behind that role in this environment, and a screen that guessed
 * would be describing a different grant from the one being made. Denies are listed
 * separately because an ALLOW does not survive one, and `exceedsDelegator` is
 * shown even when empty — an empty list is the reassurance, and hiding the section
 * when it is empty removes the only way to tell it was checked.
 */
function ReviewDialog({
  delegation,
  onClose,
  locale,
}: {
  delegation: Delegation | null;
  onClose: () => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const delegationId = delegation?.id ?? '';
  const fetcher = useCallback(() => platformApi.delegations.review(delegationId), [delegationId]);
  const { state, reload } = useAsyncData<InheritedPermissionReview>(
    `delegation-review:${delegationId}`,
    fetcher,
    delegationId.length > 0,
  );

  if (!delegation) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.delegations.reviewTitle')}
      description={t('platform.delegations.reviewLead', {
        who: delegation.delegateName ?? delegation.delegatePersonId,
      })}
    >
      <AsyncPanel state={state} onRetry={reload}>
        {(review) => (
          <div className={styles.auditDetail}>
            <dl className={styles.detailGrid}>
              <dt>{t('platform.delegations.fieldRole')}</dt>
              <dd>{review.delegatedRoleCode}</dd>
              <dt>{t('platform.delegations.scope')}</dt>
              <dd>
                {review.institutionId ?? '—'}
                {review.campusId ? ` · ${review.campusId}` : ''}
              </dd>
              <dt>{t('platform.delegations.validity')}</dt>
              <dd>
                {formatDateTime(review.validFrom, locale)} → {formatDateTime(review.validUntil, locale)}
              </dd>
              <dt>{t('platform.delegations.fieldLimit')}</dt>
              <dd>
                {typeof review.approvalLimit === 'number'
                  ? `${review.approvalLimit} ${review.currency ?? ''}`.trim()
                  : t('platform.delegations.noLimit')}
              </dd>
            </dl>

            <section>
              <h3 className={styles.subheading}>{t('platform.delegations.inherited')}</h3>
              {review.inheritedPermissions.length === 0 ? (
                <p className={styles.sectionEmpty}>{t('platform.delegations.noInherited')}</p>
              ) : (
                <ul className={styles.codeList}>
                  {review.inheritedPermissions.map((code) => (
                    <li key={code}>{code}</li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className={styles.subheading}>{t('platform.delegations.denied')}</h3>
              <p className={styles.fieldHint}>{t('platform.delegations.deniedHint')}</p>
              {review.deniedPermissions.length === 0 ? (
                <p className={styles.sectionEmpty}>{t('platform.delegations.noDenied')}</p>
              ) : (
                <ul className={styles.codeListDenied}>
                  {review.deniedPermissions.map((code) => (
                    <li key={code}>{code}</li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className={styles.subheading}>{t('platform.delegations.exceeds')}</h3>
              {review.exceedsDelegator.length === 0 ? (
                <p className={styles.okNotice} role="status">
                  {t('platform.delegations.noExceeds')}
                </p>
              ) : (
                <>
                  <p className={styles.rowDetailError} role="alert">
                    {t('platform.delegations.exceedsWarning')}
                  </p>
                  <ul className={styles.codeListDenied}>
                    {review.exceedsDelegator.map((code) => (
                      <li key={code}>{code}</li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          </div>
        )}
      </AsyncPanel>

      <div className="modal-actions">
        <button type="button" className="modal-button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );
}

/* ─── Approve ─── */

/**
 * Approval needs the approver's own person id.
 *
 * The endpoint takes it in the body rather than deriving it from the token, so the
 * console looks it up from /me and shows it read-only. It is displayed rather than
 * hidden because the operator is about to put their name on someone else's
 * authority and should see which identity is being recorded.
 */
function ApproveDialog({
  delegation,
  onClose,
  onApproved,
  onError,
}: {
  delegation: Delegation | null;
  onClose: () => void;
  onApproved: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [personId, setPersonId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!delegation) return;
    let active = true;
    setError(null);
    platformApi
      .me()
      .then((person) => {
        if (active) setPersonId(person.personId ?? '');
      })
      .catch(() => {
        // Not fatal: the field stays editable and says why it is empty.
        if (active) setPersonId('');
      });
    return () => {
      active = false;
    };
  }, [delegation]);

  if (!delegation) return null;

  const submit = async () => {
    if (personId.trim().length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.delegations.approve(delegation.id, personId.trim());
      onApproved();
    } catch (cause) {
      const message = (cause as Error).message;
      setError(message);
      onError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.delegations.approveTitle')}
      description={t('platform.delegations.approveLead', {
        who: delegation.delegateName ?? delegation.delegatePersonId,
        role: delegation.delegatedRoleCode ?? '',
      })}
      closeOnBackdropClick={false}
    >
      <div className={styles.dialogForm}>
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('platform.delegations.approveNotice')}
        </p>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.delegations.approverLabel')}</span>
          <input
            className={styles.input}
            value={personId}
            onChange={(event) => setPersonId(event.target.value)}
            aria-describedby="delegation-approver-hint"
          />
          <span className={styles.fieldHint} id="delegation-approver-hint">
            {t('platform.delegations.approverHint')}
          </span>
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="modal-button"
            disabled={personId.trim().length === 0 || submitting}
            onClick={submit}
          >
            {submitting ? t('platform.confirm.submitting') : t('platform.delegations.approve')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─── Revoke ─── */

function RevokeDialog({
  delegation,
  onClose,
  onRevoked,
}: {
  delegation: Delegation | null;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!delegation) return null;

  const run = async (reason: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const person = await platformApi.me().catch(() => ({ personId: undefined }));
      await platformApi.delegations.revoke(delegation.id, person.personId ?? '', reason);
      onRevoked();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmActionDialog
      open
      title={t('platform.delegations.revokeTitle')}
      intro={t('platform.delegations.revokeIntro', {
        who: delegation.delegateName ?? delegation.delegatePersonId,
        role: delegation.delegatedRoleCode ?? '',
      })}
      tone="critical"
      impactStatus="ready"
      impact={
        <ul className={styles.impactList}>
          <li>{t('platform.delegations.revokeImpactAccess')}</li>
          <li>{t('platform.delegations.revokeImpactImmediate')}</li>
          <li>{t('platform.delegations.revokeImpactAudit')}</li>
        </ul>
      }
      confirmLabel={t('platform.delegations.revoke')}
      submitting={submitting}
      error={error}
      onCancel={onClose}
      onConfirm={run}
    />
  );
}
